import { FC } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';

import useTheme from '@base/useTheme';
import { useFormatting } from '@providers/FormattingProvider';
import { ColorBy, ChartLabelAlign, Pattern } from '@common/api';
import { IsolateAbsolute } from '@components/layout/Flex';
import { CHART_BORDER_WIDTH, DEFAULT_CHART, LEGEND_HEIGHT } from './constants';
import {
    formatPieTooltip,
    getContrastColor,
    getHatchedGradientStops,
    getTooltipCss,
    makeFormatDataLabel,
    makeFormatPercentageLabel,
} from './formatters';
import { ChartData } from './types';

const COVER_STYLE = {
    width: '100%',
    height: '100%',
};

type EChartProps = Partial<ChartData>;

const ALIGN = {
    [ChartLabelAlign.Auto]: 'outside',
    [ChartLabelAlign.Start]: 'center',
    [ChartLabelAlign.Middle]: 'inside',
    [ChartLabelAlign.End]: 'outside',
};

export const EChartPie: FC<EChartProps> = (props: EChartProps) => {
    const {
        data = [],
        display = DEFAULT_CHART.display,
        spacing = DEFAULT_CHART.spacing,
        background = 'white',
        colorBlind = false,
    } = props;

    const theme = useTheme();
    const formatting = useFormatting();

    const positiveSeries = data.filter((series) => {
        const d = series.data[0];
        return d && (d.y as number) > 0;
    });

    const totalValue = positiveSeries.reduce((a, b) => a + (b.data[0]?.y || 0), 0);
    const showLabels = positiveSeries.some((series) => series.style.showLabel);

    const [mainSeries] = positiveSeries;

    const labelColor = mainSeries?.style.labelColor ?? theme.colors.ui.dark;
    const labelBackground = getContrastColor(labelColor, background);
    const labelPosition = ALIGN[mainSeries?.style.labelAlign];
    const labelOutline = mainSeries?.style.labelOutline;

    const shouldAutoColorLabels = labelPosition === 'inside' && !labelOutline;

    const fontStyles = {
        fontWeight: mainSeries?.style.labelBold ? 'bold' : 'normal',
        fontStyle: mainSeries?.style.labelItalic ? 'italic' : 'normal',
        fontSize: mainSeries?.style.labelFontSize,
        fontFamily: 'Inter',
    };

    const allSeries = [
        {
            type: 'pie',
            center: ['50%', '45%'],
            radius: showLabels ? ['35%', '60%'] : ['45%', '80%'],
            data: positiveSeries.map((series) => {
                const {
                    data,
                    label,
                    style: { color },
                } = series;

                const d = data[0];
                return (
                    d && {
                        name: label,
                        value: d.y,
                        formattedValue: d.label,
                        label: `${d.label} (${makeFormatPercentageLabel(formatting)(d.y, totalValue)})`,
                        itemStyle: {
                            borderWidth: CHART_BORDER_WIDTH[color.borderStyle],
                            borderColor: color.colorBy === ColorBy.Static ? color.border : d.color,
                            patternType: color.pattern,
                            color:
                                color.pattern === Pattern.None
                                    ? background
                                    : // As the pie chart is a single series,
                                    // we implement hatching as a per-item gradient instead of a per-series decal
                                    color.pattern === Pattern.Hatched
                                    ? {
                                          type: 'linear',
                                          x: 0,
                                          y: 0,
                                          x2: 1,
                                          y2: 1,
                                          colorStops: getHatchedGradientStops(d.color, background),
                                      }
                                    : d.color,
                        },
                    }
                );
            }),
            label: {
                show: showLabels,
                fontFamily: 'Inter',
                padding: mainSeries?.style.labelOffset[0],

                formatter: makeFormatDataLabel('formattedValue', shouldAutoColorLabels),

                color: labelColor,
                rotate: mainSeries?.style.labelAngle,
                fontSize: mainSeries?.style.labelFontSize,

                rich: {
                    dark: {
                        color: labelColor,
                        textBorderWidth: 0,
                        ...fontStyles,
                    },
                    light: {
                        color: labelBackground,
                        textBorderWidth: 0,
                        ...fontStyles,
                    },
                    plain: {
                        color: labelColor,
                        textBorderWidth: 4,
                        textBorderColor: background,
                        ...fontStyles,
                    },
                },

                position: labelPosition,
            },
        },
    ];

    const legendData = positiveSeries.map((series) => series.label);

    const echartConfig = {
        animation: true,
        animationDuration: 500,
        tooltip: {
            confine: true,
            trigger: 'item',
            formatter: formatPieTooltip,
            extraCssText: getTooltipCss(theme),
        },
        legend: {
            show: display.showLegend,
            bottom: 0,
            type: 'scroll',
            data: legendData,
            textStyle: {
                fontFamily: 'Inter',
            },
        },
        grid: {
            left: spacing.marginLeft,
            right: spacing.marginRight,
            top: spacing.marginTop,
            bottom: spacing.marginBottom + (display.showLegend ? LEGEND_HEIGHT : 0),
            containLabel: true,
        },
        series: allSeries,
        aria: {
            enabled: true,
            decal: {
                show: colorBlind,
            },
        },
    };

    return data.length ? (
        <IsolateAbsolute>
            <ReactEChartsCore
                echarts={echarts}
                option={echartConfig}
                style={COVER_STYLE}
                lazyUpdate={true}
                notMerge={true}
            />
        </IsolateAbsolute>
    ) : null;
};
